import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  GraduationCap,
  Pencil,
  Search,
  ShieldCheck,
  Upload,
  UserCog,
  UserPlus2,
  Users,
} from 'lucide-react';

import HelmetTitle from '@/components/helmet-title';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { useTenant } from '@/hooks';
import {
  useAddClientUserMutation,
  useAddClientUsersInBulkMutation,
  useAddStudentToBoardMutation,
  useAddTeacherToBoardMutation,
  useDeleteClientUserMutation,
  useGetClientBoardsQuery,
  useGetClientOrganizationsQuery,
  useGetAllClientUserListQuery,
  useUpdateClientUserRolesMutation,
} from '@/state';
import type { User } from '@/types';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { ORGANIZATION_TYPES } from '@/types/user-organization.types';
import { cn, getInitials } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import type {
  InviteRoleCard,
  InviteRoleOption,
  NormalizedRole,
} from './components/manage-users/types';
import UserRoleDialog from './components/manage-users/user-role-dialog';
import UserActionsMenu from './components/manage-users/user-actions-menu';
import DeleteUserAlertDialog from './components/manage-users/delete-user-alert-dialog';
import CsvUploadDialog from './components/manage-users/csv-upload-dialog';
import ManageBoardsDialog from './components/manage-users/manage-boards-dialog';

type RoleTab = 'admins' | 'instructors' | 'learners' | 'users';

const ROLE_TAB_META: Record<
  RoleTab,
  {
    title: string;
    description: string;
  }
> = {
  admins: {
    title: 'Admins',
    description: 'School administrators with org-level management access.',
  },
  instructors: {
    title: 'Instructors',
    description: 'Teachers and instructors who lead learning experiences.',
  },
  learners: {
    title: 'Learners',
    description: 'Students and learners enrolled in your organization.',
  },
  users: {
    title: 'Users',
    description: 'Organization members with standard user access.',
  },
};

const SCHOOL_ORGANIZATION_TYPES = new Set(
  ORGANIZATION_TYPES.slice(0, 4).map(
    (organizationType) => organizationType.value
  )
);

const normalizeRole = (
  role: string,
  isSchoolOrganization: boolean
): NormalizedRole | null => {
  const normalized = role.trim().toUpperCase().replace(/\s+/g, '_');
  if (
    normalized === 'SCHOOL_ADMIN' ||
    normalized === 'ORG_ADMIN' ||
    normalized === 'ADMIN'
  ) {
    return 'admin';
  }
  if (!isSchoolOrganization && normalized === 'USER') return 'user';
  if (
    isSchoolOrganization &&
    (normalized === 'TEACHER' || normalized === 'INSTRUCTOR')
  ) {
    return 'instructor';
  }
  if (
    isSchoolOrganization &&
    (normalized === 'STUDENT' || normalized === 'LEARNER')
  ) {
    return 'learner';
  }
  return null;
};

const getPrimaryRole = (
  user: User,
  isSchoolOrganization: boolean
): NormalizedRole => {
  const allRoles = [
    ...(user.user_in_client_roles ?? []),
    ...(user.roles ?? []),
  ];
  const matched = allRoles
    .map((role) => normalizeRole(role, isSchoolOrganization))
    .find(Boolean);
  return (
    (matched as NormalizedRole) ?? (isSchoolOrganization ? 'learner' : 'user')
  );
};

const getRoleBadgeClassName = (role: NormalizedRole) => {
  if (role === 'admin') {
    return 'bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 dark:text-sky-300';
  }
  if (role === 'instructor') {
    return 'bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 dark:text-violet-300';
  }
  if (role === 'user') {
    return 'bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300';
  }
  return 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300';
};

const getUserDisplayName = (user: User) => {
  const first = user.profile?.first_name?.trim() ?? '';
  const last = user.profile?.last_name?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  return user.email.split('@')[0];
};

const roleToTab = (
  role: NormalizedRole,
  isSchoolOrganization: boolean
): RoleTab => {
  if (role === 'admin') return 'admins';
  if (!isSchoolOrganization) return 'users';
  if (role === 'instructor') return 'instructors';
  return 'learners';
};

const inviteRoleToTab = (
  role: InviteRoleOption,
  isSchoolOrganization: boolean
): RoleTab => {
  if (role === 'SCHOOL ADMIN' || role === 'ORG ADMIN') return 'admins';
  if (!isSchoolOrganization) return 'users';
  if (role === 'TEACHER') return 'instructors';
  return 'learners';
};

const schoolInviteRoleCards: InviteRoleCard[] = [
  { key: 'STUDENT', label: 'Learner', icon: Users },
  { key: 'TEACHER', label: 'Instructor', icon: GraduationCap },
  { key: 'SCHOOL ADMIN', label: 'Admin', icon: ShieldCheck },
];

const companyInviteRoleCards: InviteRoleCard[] = [
  { key: 'TEACHER', label: 'User', icon: Users },
  { key: 'SCHOOL ADMIN', label: 'Admin', icon: ShieldCheck },
];

const ManageUsers = () => {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState<RoleTab>('admins');
  const [searchTerm, setSearchTerm] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] =
    useState<InviteRoleOption>('SCHOOL ADMIN');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState<InviteRoleOption>('SCHOOL ADMIN');
  const [deleteTargetUser, setDeleteTargetUser] = useState<User | null>(null);
  const [isBoardsDialogOpen, setIsBoardsDialogOpen] = useState(false);
  const [managingBoardsUser, setManagingBoardsUser] = useState<User | null>(
    null
  );
  const { data: organizations = [] } = useGetClientOrganizationsQuery();

  const {
    data: users = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAllClientUserListQuery(tenant ? { tenant } : skipToken);
  const [addClientUser, { isLoading: isInvitingUser }] =
    useAddClientUserMutation();
  const [updateClientUserRoles, { isLoading: isUpdatingUserRole }] =
    useUpdateClientUserRolesMutation();
  const [deleteClientUser, { isLoading: isDeletingUser }] =
    useDeleteClientUserMutation();
  const [addClientUsersInBulk, { isLoading: isUploadingCsv }] =
    useAddClientUsersInBulkMutation();
  const [addTeacherToBoard, { isLoading: isMovingTeacherToBoard }] =
    useAddTeacherToBoardMutation();
  const [addStudentToBoard, { isLoading: isMovingStudentToBoard }] =
    useAddStudentToBoardMutation();
  const currentOrganization = useMemo(
    () => organizations.find((org) => org?.client?.schema_name === tenant),
    [organizations, tenant]
  );
  const isSchoolOrganization = Boolean(
    currentOrganization?.client?.organization_type &&
    SCHOOL_ORGANIZATION_TYPES.has(currentOrganization.client.organization_type)
  );
  const { data: allBoards = [], isLoading: isLoadingAllBoards } =
    useGetClientBoardsQuery(
      tenant && isSchoolOrganization
        ? { tenant, boardEnrollStudent: '', teacherBoardTeacher: '' }
        : skipToken
    );
  const roleTabs = useMemo(
    () =>
      (isSchoolOrganization
        ? ['admins', 'instructors', 'learners']
        : ['admins', 'users']) as RoleTab[],
    [isSchoolOrganization]
  );

  const inviteRoleCards = isSchoolOrganization
    ? schoolInviteRoleCards
    : companyInviteRoleCards;

  useEffect(() => {
    if (!roleTabs.includes(activeTab)) {
      setActiveTab('admins');
    }
  }, [activeTab, roleTabs]);

  useEffect(() => {
    setInviteRole(isSchoolOrganization ? 'TEACHER' : 'TEACHER');
    setEditRole(isSchoolOrganization ? 'SCHOOL ADMIN' : 'SCHOOL ADMIN');
  }, [isSchoolOrganization]);

  const usersWithRole = useMemo(
    () =>
      users.map((user) => ({
        user,
        role: getPrimaryRole(user, isSchoolOrganization),
      })),
    [isSchoolOrganization, users]
  );
  const managingBoardsRole = useMemo(
    () =>
      managingBoardsUser && isSchoolOrganization
        ? getPrimaryRole(managingBoardsUser, isSchoolOrganization)
        : null,
    [isSchoolOrganization, managingBoardsUser]
  );
  const joinedBoardsQueryArg =
    tenant &&
    isSchoolOrganization &&
    managingBoardsUser &&
    managingBoardsRole &&
    managingBoardsRole !== 'admin'
      ? managingBoardsRole === 'learner'
        ? {
            tenant,
            boardEnrollStudent: managingBoardsUser.id,
            teacherBoardTeacher: '',
          }
        : {
            tenant,
            boardEnrollStudent: '',
            teacherBoardTeacher: managingBoardsUser.id,
          }
      : skipToken;
  const { data: joinedBoards = [], isLoading: isLoadingJoinedBoards } =
    useGetClientBoardsQuery(joinedBoardsQueryArg);
  const joinedBoardIds = useMemo(
    () => new Set(joinedBoards.map((board) => board.id)),
    [joinedBoards]
  );

  const tabCounts = useMemo(() => {
    const initialCounts = isSchoolOrganization
      ? { admins: 0, instructors: 0, learners: 0, users: 0 }
      : { admins: 0, instructors: 0, learners: 0, users: 0 };

    return usersWithRole.reduce(
      (acc, entry) => {
        const tab = roleToTab(entry.role, isSchoolOrganization);
        acc[tab] += 1;
        return acc;
      },
      initialCounts as Record<RoleTab, number>
    );
  }, [isSchoolOrganization, usersWithRole]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return usersWithRole
      .filter(
        (entry) => roleToTab(entry.role, isSchoolOrganization) === activeTab
      )
      .filter(({ user }) => {
        if (!normalizedSearch) return true;
        const userName = getUserDisplayName(user).toLowerCase();
        return (
          user.email.toLowerCase().includes(normalizedSearch) ||
          userName.includes(normalizedSearch)
        );
      });
  }, [activeTab, isSchoolOrganization, searchTerm, usersWithRole]);

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteRole(isSchoolOrganization ? 'TEACHER' : 'USER');
  };

  const normalizedToInviteRole = (role: NormalizedRole): InviteRoleOption => {
    if (role === 'admin')
      return isSchoolOrganization ? 'SCHOOL ADMIN' : 'ORG ADMIN';
    if (isSchoolOrganization) {
      if (role === 'instructor') return 'TEACHER';
      return 'STUDENT';
    }
    return 'USER';
  };

  const handleInviteSubmit = async () => {
    if (!tenant || isInvitingUser) return;
    const email = inviteEmail.trim();
    if (!email) {
      toast.error('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      await addClientUser({
        tenant,
        data: {
          email,
          user_roles: [inviteRole],
        },
      }).unwrap();

      toast.success('User invited successfully.');
      setIsInviteDialogOpen(false);
      resetInviteForm();
      setActiveTab(inviteRoleToTab(inviteRole, isSchoolOrganization));
    } catch (err) {
      const apiErr = err as ApiErrorResponse;
      const message =
        typeof apiErr?.data === 'string'
          ? apiErr.data
          : 'Unable to invite user. Please try again.';
      toast.error(message);
    }
  };

  const openEditDialog = (user: User) => {
    const currentRole = getPrimaryRole(user, isSchoolOrganization);
    setEditingUser(user);
    setEditRole(normalizedToInviteRole(currentRole));
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!tenant || !editingUser || isUpdatingUserRole) return;
    try {
      await updateClientUserRoles({
        tenant,
        roleId: editingUser.role_id,
        data: { user_roles: [editRole] },
      }).unwrap();
      toast.success('User role updated successfully.');
      setIsEditDialogOpen(false);
      setActiveTab(inviteRoleToTab(editRole, isSchoolOrganization));
      setEditingUser(null);
    } catch (err) {
      const apiErr = err as ApiErrorResponse;
      const message =
        typeof apiErr?.data === 'string'
          ? apiErr.data
          : 'Unable to update user role. Please try again.';
      toast.error(message);
    }
  };

  const handleDeleteUser = async () => {
    if (!tenant || !deleteTargetUser || isDeletingUser) return;
    try {
      await deleteClientUser({
        tenant,
        userId: deleteTargetUser.id,
      }).unwrap();
      toast.success('User removed successfully.');
      setDeleteTargetUser(null);
    } catch (err) {
      const apiErr = err as ApiErrorResponse;
      const message =
        typeof apiErr?.data === 'string'
          ? apiErr.data
          : 'Unable to remove user. Please try again.';
      toast.error(message);
    }
  };

  const handleBulkUsersUpload = async (file: File) => {
    if (!tenant || isUploadingCsv) return;
    const formData = new FormData();
    formData.append('document', file);

    try {
      await addClientUsersInBulk({
        tenant,
        data: formData,
      }).unwrap();
      toast.success('Users uploaded successfully.');
      setIsCsvDialogOpen(false);
    } catch (err) {
      const apiErr = err as ApiErrorResponse;
      const message =
        typeof apiErr?.data === 'string'
          ? apiErr.data
          : 'Unable to upload CSV file. Please try again.';
      toast.error(message);
    }
  };

  const openBoardsDialog = (user: User) => {
    if (!isSchoolOrganization) return;
    setManagingBoardsUser(user);
    setIsBoardsDialogOpen(true);
  };

  const handleMoveToBoard = async (boardId: number) => {
    if (
      !tenant ||
      !isSchoolOrganization ||
      !managingBoardsUser ||
      !managingBoardsRole ||
      managingBoardsRole === 'admin'
    ) {
      return;
    }

    try {
      if (managingBoardsRole === 'learner') {
        await addStudentToBoard({
          tenant,
          data: {
            students: [
              {
                student: managingBoardsUser.id,
                board: boardId,
                semester_course: null,
                active: true,
              },
            ],
          },
        }).unwrap();
      } else {
        await addTeacherToBoard({
          tenant,
          data: {
            teachers: [
              {
                teacher: managingBoardsUser.id,
                board: boardId,
                active: true,
              },
            ],
          },
        }).unwrap();
      }
      toast.success('User moved to board successfully.');
    } catch (err) {
      const apiErr = err as ApiErrorResponse;
      const message =
        typeof apiErr?.data === 'string'
          ? apiErr.data
          : 'Unable to move user to board. Please try again.';
      toast.error(message);
    }
  };

  return (
    <>
      <HelmetTitle title="Manage Users" />
      <WithLoadingErrorWrapper
        loading={isLoading}
        error={error as ApiErrorResponse}
        isFetching={isFetching}
        onRetry={refetch}
      >
        <div className="mx-auto w-full max-w-[1480px] space-y-6 p-6">
          <section className="rounded-2xl border border-border/50 bg-card/70 p-5 backdrop-blur-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  <UserCog className="h-3.5 w-3.5" />
                  Admin Tools
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight">
                    Manage Users
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Search, review, and organize users by role in one place.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setIsInviteDialogOpen(true)}
                >
                  <UserPlus2 className="h-4 w-4" />
                  Invite User
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setIsCsvDialogOpen(true)}
                >
                  <Upload className="h-4 w-4" />
                  Upload .csv file
                </Button>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {roleTabs.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-primary/50 bg-primary/10 text-primary'
                        : 'border-border/60 bg-card text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <span>{ROLE_TAB_META[tab].title}</span>
                    <span
                      className={cn(
                        'inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-xs',
                        isActive
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            <Card className="overflow-hidden border-border/50 bg-card/80 p-0">
              <div className="border-b border-border/50 bg-muted/20 px-4 py-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {ROLE_TAB_META[activeTab].title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {ROLE_TAB_META[activeTab].description}
                    </p>
                  </div>

                  <div className="relative w-full max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search by name or email"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <CardContent className="min-h-[62vh] p-0">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead className="bg-muted/30 text-muted-foreground">
                      <tr>
                        <th className="px-5 py-3 text-left font-medium">
                          User
                        </th>
                        <th className="px-5 py-3 text-left font-medium">
                          Email
                        </th>
                        <th className="px-5 py-3 text-left font-medium">
                          Role
                        </th>
                        <th className="px-5 py-3 text-left font-medium">
                          Joined
                        </th>
                        <th className="px-5 py-3 text-left font-medium">
                          Last Login
                        </th>
                        <th className="px-5 py-3 text-right font-medium">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-5 py-10 text-center text-muted-foreground"
                          >
                            No users found for this filter.
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map(({ user, role }) => {
                          const fullName = getUserDisplayName(user);
                          const photo =
                            user.profile?.thumbnail || user.profile?.photo_url;
                          const canManageBoards =
                            isSchoolOrganization &&
                            (role === 'instructor' || role === 'learner');
                          return (
                            <tr
                              key={user.id}
                              className="border-t border-border/40 transition-colors hover:bg-muted/20"
                            >
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-9 w-9 rounded-lg border border-border/50">
                                    <AvatarImage src={photo || undefined} />
                                    <AvatarFallback className="rounded-lg text-xs font-semibold">
                                      {getInitials(fullName)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium">{fullName}</p>
                                    <p className="text-xs text-muted-foreground">
                                      ID: {user.role_id}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-muted-foreground">
                                {user.email}
                              </td>
                              <td className="px-5 py-4">
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize',
                                    getRoleBadgeClassName(role)
                                  )}
                                >
                                  {role}
                                </Badge>
                              </td>
                              <td className="px-5 py-4 text-muted-foreground">
                                {user.created_on || '-'}
                              </td>
                              <td className="px-5 py-4 text-muted-foreground">
                                {user.last_login || '-'}
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex justify-end gap-2">
                                  <UserActionsMenu
                                    onBoards={
                                      canManageBoards
                                        ? () => openBoardsDialog(user)
                                        : undefined
                                    }
                                    showBoards={canManageBoards}
                                    onEdit={() => openEditDialog(user)}
                                    onRemove={() => setDeleteTargetUser(user)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </WithLoadingErrorWrapper>

      <UserRoleDialog
        open={isInviteDialogOpen}
        onOpenChange={(isOpen) => {
          setIsInviteDialogOpen(isOpen);
          if (!isOpen) resetInviteForm();
        }}
        title="Invite User"
        email={inviteEmail}
        onEmailChange={setInviteEmail}
        selectedRole={inviteRole}
        onRoleChange={setInviteRole}
        roleCards={inviteRoleCards}
        onSubmit={handleInviteSubmit}
        submitLabel="Invite User"
        submittingLabel="Inviting..."
        submitIcon={UserPlus2}
        isSubmitting={isInvitingUser}
        isSubmitDisabled={!inviteEmail.trim()}
      />

      <UserRoleDialog
        open={isEditDialogOpen}
        onOpenChange={(isOpen) => {
          setIsEditDialogOpen(isOpen);
          if (!isOpen) setEditingUser(null);
        }}
        title="Edit User"
        email={editingUser?.email ?? ''}
        emailDisabled
        selectedRole={editRole}
        onRoleChange={setEditRole}
        roleCards={inviteRoleCards}
        onSubmit={handleEditSubmit}
        submitLabel="Update Role"
        submittingLabel="Updating..."
        submitIcon={Pencil}
        isSubmitting={isUpdatingUserRole}
        isSubmitDisabled={!editingUser}
      />

      <CsvUploadDialog
        open={isCsvDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isUploadingCsv) setIsCsvDialogOpen(isOpen);
        }}
        onSubmit={handleBulkUsersUpload}
        isSubmitting={isUploadingCsv}
      />

      <ManageBoardsDialog
        open={isBoardsDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isMovingStudentToBoard && !isMovingTeacherToBoard) {
            setIsBoardsDialogOpen(isOpen);
          }
          if (!isOpen) setManagingBoardsUser(null);
        }}
        user={managingBoardsUser}
        normalizedRole={managingBoardsRole}
        boards={allBoards}
        joinedBoardIds={joinedBoardIds}
        isLoadingBoards={isLoadingAllBoards || isLoadingJoinedBoards}
        isMovingToBoard={isMovingStudentToBoard || isMovingTeacherToBoard}
        onMoveToBoard={handleMoveToBoard}
      />

      <DeleteUserAlertDialog
        open={!!deleteTargetUser}
        userLabel={
          deleteTargetUser
            ? `${getUserDisplayName(deleteTargetUser)} (${deleteTargetUser.email})`
            : null
        }
        isDeleting={isDeletingUser}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isDeletingUser) setDeleteTargetUser(null);
        }}
        onConfirm={handleDeleteUser}
      />
    </>
  );
};

export default ManageUsers;
